import clsx from 'clsx';
import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { useEffect, useId, useState } from 'react';
import { mergeStyling } from '../common-functions';
import OdinIcon from '../odin-icon/odin-icon.component';

export interface AccordionProps {
  id?: string;
  isOpen: boolean;
  title: string | ReactNode;
  children: ReactNode;
  hidden?: boolean;
  onClick?: (isOpen: boolean) => void;
  wrapperStyles?: string;
  headerStyles?: string;
  childWrapperStyles?: string;
}

export default function Accordion({
  id,
  isOpen,
  title,
  children,
  hidden,
  onClick,
  wrapperStyles,
  headerStyles,
  childWrapperStyles,
}: AccordionProps) {
  const contentWrapperId = useId();
  const [currentlyOpen, setCurrentlyOpen] = useState(isOpen);

  useEffect(() => {
    if (isOpen !== currentlyOpen) {
      setCurrentlyOpen(isOpen);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only sync when isOpen prop changes, not when currentlyOpen changes
  }, [isOpen]);

  const onAccordionClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    onClick?.(!currentlyOpen);
    setCurrentlyOpen(!currentlyOpen);
  };

  const accordionWrapperStyles = clsx('odin-mb-4 odin-flex odin-w-full odin-flex-col', {
    'odin-hidden': hidden,
  });

  const accordionHeaderStyles = mergeStyling(
    'odin-flex odin-border-b odin-border-solid odin-border-gray-2 focus:odin-outline-none focus:odin-border-primary-3 focus:odin-border-b-2 focus:-odin-mb-0.5',
    headerStyles
  );

  const childWrapperStyling = mergeStyling('odin-grow odin-pt-5', childWrapperStyles);

  const defaultStyle: CSSProperties = {
    maxHeight: currentlyOpen ? '100000px' : '0px',
    display: currentlyOpen ? '' : 'none',
  };

  return (
    <div id={id} className={mergeStyling(accordionWrapperStyles, wrapperStyles)}>
      <a href={`#${contentWrapperId}`} className={accordionHeaderStyles} onClick={onAccordionClick}>
        <div className="odin-grow odin-text-lg odin-leading-9 odin-font-semibold">{title}</div>
        <div className="odin-flex odin-shrink odin-items-center odin-pr-px">
          <OdinIcon icon={currentlyOpen ? 'ChevronUp' : 'ChevronDown'} />
        </div>
      </a>
      <div id={contentWrapperId} className={childWrapperStyling} style={defaultStyle}>
        {children}
      </div>
    </div>
  );
}
